import { useAdjustQuestionsPValuesMutation, useGetPAnalysisCellsQuery } from '@report/graphql';
import { ConfirmationModal } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { toDayRange } from '@shared/utils';

import { useItemAnalysisSearchParams } from '../../hooks';
import { getDifficultyLevelDiffCount } from '../../utils';

interface PValuesAdjustmentConfirmationModalProps {
  open: boolean;
  onClose: () => void;
}

export default function PValuesAdjustmentConfirmationModal(
  props: Readonly<PValuesAdjustmentConfirmationModalProps>,
) {
  const { open, onClose } = props;

  const { showSnackbar } = useSnackbar();

  const [
    {
      startDate,
      endDate,
      examCourse,
      minExaminees,
      questionStatus,
      pBufferEasyThreshold,
      pBufferHardThreshold,
      pBufferMediumThreshold,
    },
  ] = useItemAnalysisSearchParams();

  const pValueCustomThreshold = {
    hard: {
      min: 0,
      max: pBufferHardThreshold,
    },
    medium: {
      min: Number(pBufferHardThreshold + 0.01),
      max: pBufferMediumThreshold,
    },
    easy: {
      min: Number(pBufferMediumThreshold + 0.01),
      max: pBufferEasyThreshold,
    },
  };

  const isFilterSet = !!startDate && !!endDate && !!examCourse && minExaminees !== null;

  const { data: pAnalysisCellsData } = useGetPAnalysisCellsQuery({
    skip: !isFilterSet,
    variables: {
      getPAnalysisCellsInput: {
        startedAt: startDate as Date,
        endedAt: endDate as Date,
        qBankStructureId: examCourse,
        minimumExamineePerQuestion: minExaminees as number,
        pValueCustomThreshold,
      },
    },
    fetchPolicy: 'cache-first',
  });

  const pAnalysisCells = pAnalysisCellsData?.reportGetPAnalysisCells.cells ?? [];

  const difficultyLevelDiff = getDifficultyLevelDiffCount(pAnalysisCells);
  const totalDiffCount =
    difficultyLevelDiff.oneLevelDiffCount + difficultyLevelDiff.twoLevelDiffCount;

  const [adjustQuestionsPValues, { loading: isAdjustingPValues }] =
    useAdjustQuestionsPValuesMutation();

  const handleAccept = async () => {
    const { startedAt, endedAt } = toDayRange(startDate, endDate);
    await adjustQuestionsPValues({
      variables: {
        adjustQuestionsPValuesInput: {
          startAt: startedAt as Date,
          endAt: endedAt as Date,
          qBankStructureId: examCourse,
          minimumExamineePerQuestion: minExaminees as number,
          questionStatus: questionStatus,
          pValue: pValueCustomThreshold,
        },
      },
      onCompleted: () => {
        showSnackbar({
          variant: 'success',
          message: 'บันทึกรายการปรับปรุงค่า P เรียบร้อยแล้ว',
        });
        onClose();
      },
      onError: () => {
        showSnackbar({
          variant: 'error',
          message: 'เกิดข้อผิดพลาดในการบันทึกรายการปรับปรุงค่า P กรุณาลองใหม่อีกครั้ง',
        });
        onClose();
      },
    });
  };

  const handleDiscard = () => {
    onClose();
  };

  return (
    <ConfirmationModal
      open={open}
      onClose={onClose}
      title='ยืนยันการบันทึกรายการปรับปรุงค่า P'
      content={
        <>
          รายการที่แนะนำให้ปรับปรุงค่า P: {totalDiffCount} ข้อ
          <br />
          🔴 แตกต่าง 2 ระดับ: {difficultyLevelDiff.twoLevelDiffCount} ข้อ
          <br />
          🟡 แตกต่าง 1 ระดับ: {difficultyLevelDiff.oneLevelDiffCount} ข้อ
        </>
      }
      acceptText='ยืนยันการบันทึก'
      discardText='ยกเลิก'
      onAccept={handleAccept}
      onDiscard={handleDiscard}
      loading={isAdjustingPValues}
    />
  );
}
